<template>
  <nav class="device-group-bar" :aria-label="$t('device_groups.groups_title')">
    <div class="d-flex align-items-start gap-2">
      <!-- The nav's own label names the chips for screen readers; this is its visible twin. -->
      <span
        class="device-group-bar__label d-inline-flex align-items-center flex-shrink-0 fw-semibold text-body-tertiary"
        aria-hidden="true"
      >
        {{ $t("device_groups.filter.groups") }}
      </span>
      <ul
        class="list-unstyled mb-0 d-flex flex-wrap align-items-center gap-2 min-w-0"
      >
        <li v-for="row in rows" :key="String(row.view)">
          <button
            type="button"
            class="device-group-bar__chip"
            :class="{ 'is-active': selected === row.view }"
            :aria-current="selected === row.view ? 'true' : undefined"
            @click="selected = row.view"
          >
            <span class="text-truncate">{{ row.label }}</span>
            <span class="device-group-bar__count">{{ row.count }}</span>
          </button>
        </li>
      </ul>
    </div>

    <div
      v-if="shownSuggestions.length > 0"
      class="d-flex flex-wrap align-items-center gap-2 mt-2 fs-9"
    >
      <span class="text-body-secondary">
        {{ $t("device_groups.suggested_from_address") }}
      </span>
      <div
        v-for="suggestion in shownSuggestions"
        :key="suggestion.name"
        class="device-group-bar__suggestion"
      >
        <span class="fw-semibold text-truncate">{{ suggestion.name }}</span>
        <span class="text-body-secondary text-nowrap">
          {{ suggestionSummary(suggestion) }}
        </span>
        <button
          type="button"
          class="btn btn-link btn-sm p-0 fs-9 text-nowrap"
          :disabled="busy"
          @click="$emit('createSuggestion', suggestion)"
        >
          {{ $t("device_groups.create_group") }}
        </button>
        <button
          type="button"
          class="device-group-bar__dismiss"
          :aria-label="
            $t('device_groups.dismiss_suggestion', { name: suggestion.name })
          "
          :title="$t('device_groups.dismiss')"
          @click="$emit('dismissSuggestion', suggestion)"
        >
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import {
  BAR_SUGGESTION_LIMIT,
  devicesPowerSummary,
  type AddressSuggestion,
  type DeviceCountsByGroup,
} from "@/dashboard/production_strategy/device_groups/deviceGroupUtils";
import type {
  DeviceGroup,
  DeviceGroupView,
} from "@/dashboard/production_strategy/device_groups/deviceGroupsTypes";
import type { RegistryDevice } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The Devices tab's group chips above the table: All devices, each group and Ungrouped, with counts
 * taken from the devices the tab shows, then the largest address suggestions for the ungrouped ones. A row
 * above the table rather than a rail beside it, so the table keeps the card's full width.
 */
const props = withDefaults(
  defineProps<{
    devices: RegistryDevice[];
    /** Each group's members among `devices`. */
    countByGroup: DeviceCountsByGroup;
    groups: DeviceGroup[];
    /** Largest first; the bar shows the first {@link BAR_SUGGESTION_LIMIT}. */
    suggestions?: AddressSuggestion[];
    /** Disables the suggestions' Create group while a change is saving. */
    busy?: boolean;
  }>(),
  { suggestions: () => [], busy: false }
);

defineEmits<{
  createSuggestion: [suggestion: AddressSuggestion];
  dismissSuggestion: [suggestion: AddressSuggestion];
}>();

const selected = defineModel<DeviceGroupView>("selected", { required: true });

const { t } = useI18n();

const rows = computed<
  { view: DeviceGroupView; label: string; count: number }[]
>(() => [
  {
    view: "all",
    label: t("device_groups.all_devices"),
    count: props.devices.length,
  },
  ...props.groups.map((group) => ({
    view: group.id,
    label: group.name,
    count: props.countByGroup[group.id] ?? 0,
  })),
  {
    view: "ungrouped",
    label: t("device_groups.ungrouped"),
    count: props.countByGroup.none ?? 0,
  },
]);

const shownSuggestions = computed(() =>
  props.suggestions.slice(0, BAR_SUGGESTION_LIMIT)
);

const suggestionSummary = (suggestion: AddressSuggestion) => {
  const ids = new Set(suggestion.deviceIds);
  return devicesPowerSummary(
    props.devices.filter((device) => ids.has(device.id)),
    t
  );
};
</script>

<style scoped lang="scss">
// As tall as a chip, so the label lines up with the first row of chips when they wrap.
.device-group-bar__label {
  height: 30px;
  font-size: 0.8rem;
}

.device-group-bar__chip {
  all: unset;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 280px;
  padding: 4px 12px;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 999px;
  background: var(--phoenix-emphasis-bg);
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    background: var(--phoenix-gray-50);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: 1px;
  }

  &.is-active {
    border-color: var(--phoenix-primary);
    background: var(--phoenix-primary-bg-subtle);
    font-weight: 600;
  }
}

.device-group-bar__count {
  color: var(--phoenix-secondary-color);
  font-variant-numeric: tabular-nums;
}

.device-group-bar__suggestion {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  padding: 2px 6px 2px 10px;
  border: 1px dashed var(--phoenix-border-color);
  border-radius: 999px;
}

.device-group-bar__dismiss {
  all: unset;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: var(--phoenix-secondary-color);
  cursor: pointer;

  &:hover {
    background: var(--phoenix-gray-100);
    color: var(--phoenix-body-color);
  }

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
  }
}
</style>
